import { Component, computed, OnInit, input, inject, output } from '@angular/core';
import { ToolJobService } from '../../../services/api-services/tools/tool-job.service';
import { isInputWithMapping, ToolInput } from '../../models/toolchain.models';
import { DataType } from '../../models/tool.models';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DndDirective } from '../../directives/drag-and-drop/dnd.directive';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { fileNameFromContentDisposition } from '../../helper';

/** MIME type used when browsers cannot identify OneNote files. */
const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
  '.one': 'application/onenote',
  '.onepkg': 'application/onenote',
};

export interface DialogData {
  supportedMediaTypes: string[];
  files: File[];
  isList: boolean;
}

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  imports: [DndDirective, MatButton, MatTooltip, MatIcon, MatMiniFabButton],
})
export class FileUploadComponent implements OnInit {
  /** Inject services. */
  private readonly toolJobService = inject(ToolJobService);
  private readonly snackBar = inject(MatSnackBar);

  readonly fileSelectedEvent = output<File[]>();

  readonly input = input<ToolInput>();

  files: File[] = [];

  readonly supportedMediaTypes = input<string[]>([]);

  /** Media types and extensions accepted by the native file picker. */
  readonly acceptedFileTypes = computed(() => {
    const mediaTypes = this.supportedMediaTypes();
    const extensions = Object.entries(MIME_TYPE_BY_EXTENSION)
      .filter(([, mediaType]) => mediaTypes.includes(mediaType))
      .map(([extension]) => extension);
    return [...mediaTypes, ...extensions].join(',');
  });

  readonly inputDetails = input<{
    displayName: string;
    description: string;
  }>({
    displayName: '',
    description: '',
  });

  readonly isList = input(false);
  protected readonly open = open;

  isSelectable = true;

  ngOnInit() {
    const input = this.input();
    if (input) {
      if (isInputWithMapping(input)) {
        this.isSelectable = input.isSelectable;
        let fileIds;
        if (Array.isArray(input.valueToMap)) {
          fileIds = input.valueToMap;
        } else {
          fileIds = [input.valueToMap];
        }
        fileIds = this.toNumberList(fileIds);

        fileIds.forEach(async (fileId, index) => {
          const res = await this.toolJobService.getOutputById(fileId);
          const contentType = (res.headers.get('content-type') ?? res.body?.type ?? '')
            .split(';')[0]
            .trim();
          const extension = contentType.split('/')[1];
          const fileExtension = extension ? `.${extension}` : '';
          let fileName =
            fileNameFromContentDisposition(res.headers.get('content-disposition')) ??
            `file_${index}${fileExtension}`;
          fileName = fileName.replace(/\//g, '_');
          this.files.push(
            new File([res.body!], fileName, {
              type: contentType,
            })
          );
          this.fileSelectedEvent.emit(this.files);
        });
      }
    }
  }

  resetInput() {
    this.files = [];
  }

  onFileDropped(fileList: FileList) {
    if (fileList.length < 0) {
      return;
    }
    const files: File[] = Array.from(fileList);
    this.addFiles(files);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const files: File[] = Array.from(input.files);
      this.addFiles(files);
    }
  }

  addFiles(uploadedFiles: File[]) {
    const files = uploadedFiles
      .map(file => this.normalizeSupportedFile(file))
      .filter((file): file is File => file !== null);

    if (files.length < uploadedFiles.length) {
      this.snackBar.open('Cannot add file. Invalid Media Type.', 'Close', {
        duration: 7000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
    }

    for (const file of files) {
      const alreadyExists = this.files.some(existing => existing.name === file.name);
      if (alreadyExists) {
        this.snackBar.open('Cannot add file. This file was already uploaded.', 'Close', {
          duration: 7000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
      }
    }

    if (files.length <= 0) {
      return;
    }

    if (!this.isList()) {
      if (files.length > 1) {
        this.snackBar.open(
          'Only one file can be uploaded. Additional files were ignored.',
          'Close',
          {
            duration: 7000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          }
        );
      }
      this.files = [files[0]];
    } else {
      this.files.push(...files);
    }
    this.fileSelectedEvent.emit(this.files);
  }

  /** Apply the declared MIME type when a supported extension is unknown to the browser. */
  private normalizeSupportedFile(file: File): File | null {
    const mediaTypes = this.supportedMediaTypes();
    if (mediaTypes.includes(file.type)) {
      return file;
    }

    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    const inferredMediaType = MIME_TYPE_BY_EXTENSION[extension];
    if (!inferredMediaType || !mediaTypes.includes(inferredMediaType)) {
      return null;
    }

    return new File([file], file.name, {
      type: inferredMediaType,
      lastModified: file.lastModified,
    });
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
    this.fileSelectedEvent.emit(this.files);
  }

  private toNumberList(data: DataType | DataType[]) {
    if (Array.isArray(data)) {
      return data.map(val => parseInt(val.toString()));
    }
    return [parseInt(data.toString())];
  }
}
